import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Undo2, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import BarcodeInput from '@/components/BarcodeInput';
import apiClient from '@/services/apiClient';
import { validateStaffId } from '@/services/validationService';
import { RecordReturnPanel } from './outwardReturns/RecordReturnPanel';
import { RedispatchPanel } from './outwardReturns/RedispatchPanel';

const useGeo = () => {
  const [geo, setGeo] = useState({ latitude: null, longitude: null });
  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(
      (p) => setGeo({ latitude: p.coords.latitude, longitude: p.coords.longitude }), () => {},
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 });
  }, []);
  return geo;
};

const StaffGate = ({ onVerified }) => {
  const ref = useRef(null);
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const verify = async (v) => {
    const res = await validateStaffId(v);
    if (!res.valid) { setError(res.message); return; }
    toast.success(res.message);
    onVerified({ id: res.staffId, name: res.staffName });
  };
  return (
    <div className="bg-white rounded-lg border border-[#E5DFD0] p-6 shadow-sm" data-testid="otr-staff-gate">
      <h2 className="text-lg font-semibold text-[#1A1F2E] mb-4 font-display">Staff Identification</h2>
      <BarcodeInput ref={ref} label="Scan or enter your Staff ID" value={value} onChange={setValue}
        onScan={verify} placeholder="Staff ID" autoFocus error={error} data-testid="otr-staff-input" />
      <Button className="mt-4 bg-[#2D5F4F] hover:bg-[#244d40]" disabled={!value.trim()} onClick={() => verify(value)} data-testid="otr-staff-submit">
        <UserCheck className="w-4 h-4 mr-2" /> Verify
      </Button>
    </div>
  );
};

const OutwardTransportReturns = () => {
  const navigate = useNavigate();
  const geo = useGeo();
  const [staff, setStaff] = useState(null);
  const [tab, setTab] = useState('record');
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  const loadPending = useCallback(async () => {
    try {
      const r = await apiClient.get('/outward/returns/pending', { params: { search: search || undefined } });
      setGroups(r.data.groups || []);
      setTotal(r.data.total_bundles || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load returned bundles');
    }
  }, [search]);

  useEffect(() => { if (staff) loadPending(); }, [staff, loadPending]);

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="otr-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] sticky top-0 z-40 shadow-lg">
        <div className="max-w-5xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/gate-entry/outward')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="otr-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display flex items-center gap-2"><Undo2 className="w-5 h-5" /> Transport Returns</h1>
              <p className="text-sm text-[#E8DCC8]">Job Worker & Internal Transfer outward</p>
            </div>
          </div>
          {staff && <span className="text-sm bg-white/10 px-3 py-1.5 rounded-lg" data-testid="otr-staff-badge">{staff.name || staff.id}</span>}
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {!staff ? <StaffGate onVerified={setStaff} /> : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="record" data-testid="otr-tab-record">Record Return</TabsTrigger>
              <TabsTrigger value="redispatch" data-testid="otr-tab-redispatch">Re-dispatch ({total})</TabsTrigger>
            </TabsList>
            <TabsContent value="record">
              <RecordReturnPanel staffId={staff.id} onRecorded={loadPending} />
            </TabsContent>
            <TabsContent value="redispatch">
              <RedispatchPanel groups={groups} search={search} setSearch={setSearch} staffId={staff.id} geo={geo} onChanged={loadPending} />
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
};

export default OutwardTransportReturns;
